/* =========================================================
   ZODIAC PRODUCT FILTER
   ========================================================= */

class ZodiacSeries {
  constructor(root) {
    this.root = root;

    this.buttons = Array.from(
      root.querySelectorAll(
        '[data-zodiac-filter]'
      )
    );

    this.products = Array.from(
      root.querySelectorAll(
        '[data-zodiac-product]'
      )
    );

    this.emptyState =
      root.querySelector(
        '[data-zodiac-empty]'
      );

    this.handleFilterClick =
      this.handleFilterClick.bind(this);

    this.init();
  }

  init() {
    this.buttons.forEach((button) => {
      button.addEventListener(
        'click',
        this.handleFilterClick
      );
    });

    this.showAll();
  }

  handleFilterClick(event) {
    const button =
      event.currentTarget;

    const selectedTag = (
      button.dataset.zodiacTag || ''
    )
      .trim()
      .toLowerCase();

    if (!selectedTag) {
      return;
    }

    if (selectedTag === 'all') {
      this.showAll();
      return;
    }

    this.updateButtons(
      selectedTag
    );

    this.filterProducts(
      selectedTag
    );
  }

  updateButtons(activeTag) {
    this.buttons.forEach((button) => {
      const buttonTag = (
        button.dataset.zodiacTag || ''
      )
        .trim()
        .toLowerCase();

      const isActive =
        buttonTag === activeTag;

      button.classList.toggle(
        'is-active',
        isActive
      );

      button.setAttribute(
        'aria-pressed',
        isActive ? 'true' : 'false'
      );
    });
  }

  filterProducts(selectedTag) {
    let visibleCount = 0;

    this.products.forEach((product) => {
      const tags =
        this.getProductTags(
          product
        );

      const visible =
        tags.includes(
          selectedTag
        );

      product.hidden =
        !visible;

      if (visible) {
        visibleCount += 1;
      }
    });

    this.updateEmptyState(
      visibleCount
    );
  }

  showAll() {
    this.updateButtons('all');

    this.products.forEach(
      (product) => {
        product.hidden = false;
      }
    );

    this.updateEmptyState(
      this.products.length
    );
  }

  getProductTags(product) {
    return (
      product.dataset.productTags || ''
    )
      .split(',')
      .map((tag) =>
        tag
          .trim()
          .toLowerCase()
      )
      .filter(Boolean);
  }

  updateEmptyState(
    visibleCount
  ) {
    if (!this.emptyState) {
      return;
    }

    this.emptyState.hidden =
      visibleCount > 0;
  }

  destroy() {
    this.buttons.forEach((button) => {
      button.removeEventListener(
        'click',
        this.handleFilterClick
      );
    });
  }
}


/* =========================================================
   ZODIAC INTRO
   ========================================================= */

class ZodiacIntro {
  constructor(section) {
    this.section = section;

    this.intro =
      section.querySelector(
        '[data-zodiac-intro]'
      );

    this.mode = (
      section.dataset.introMode ||
      'session'
    )
      .trim()
      .toLowerCase();

    this.canvas = null;
    this.ctx = null;

    this.width = 0;
    this.height = 0;

    this.centerX = 0;
    this.centerY = 0;

    this.dpr =
      Math.min(
        window.devicePixelRatio || 1,
        2
      );

    this.stars = [];

    this.animationFrame = null;

    this.startTime = null;

    this.animationDuration = 1700;

    this.finished = false;

    this.resize =
      this.resize.bind(this);

    this.render =
      this.render.bind(this);
  }

  shouldPlay() {
    if (!this.intro) {
      return false;
    }

    if (this.mode === 'off') {
      return false;
    }

    const designMode =
      Boolean(
        window.Shopify &&
        window.Shopify.designMode
      );

    if (
      this.mode === 'session' &&
      !designMode
    ) {
      try {
        return (
          sessionStorage.getItem(
            'espinaZodiacIntroSeen'
          ) !== 'true'
        );
      } catch (error) {
        return true;
      }
    }

    return true;
  }

  markSeen() {
    const designMode =
      Boolean(
        window.Shopify &&
        window.Shopify.designMode
      );

    if (
      this.mode !== 'session' ||
      designMode
    ) {
      return;
    }

    try {
      sessionStorage.setItem(
        'espinaZodiacIntroSeen',
        'true'
      );
    } catch (error) {
      /* no-op */
    }
  }

  prepare() {
    if (!this.intro) {
      return false;
    }

    if (
      this.intro.parentElement !==
      document.body
    ) {
      document.body.appendChild(
        this.intro
      );
    }

    this.canvas =
      this.intro.querySelector(
        '[data-zodiac-intro-canvas]'
      );

    if (!this.canvas) {
      return false;
    }

    this.ctx =
      this.canvas.getContext(
        '2d',
        {
          alpha: false
        }
      );

    return Boolean(
      this.ctx
    );
  }

  resize() {
    if (
      !this.canvas ||
      !this.ctx
    ) {
      return;
    }

    this.width =
      window.innerWidth;

    this.height =
      window.innerHeight;

    this.centerX =
      this.width / 2;

    this.centerY =
      this.height / 2;

    this.canvas.width =
      Math.round(
        this.width *
        this.dpr
      );

    this.canvas.height =
      Math.round(
        this.height *
        this.dpr
      );

    this.canvas.style.width =
      `${this.width}px`;

    this.canvas.style.height =
      `${this.height}px`;

    this.ctx.setTransform(
      this.dpr,
      0,
      0,
      this.dpr,
      0,
      0
    );
  }

  createStars() {
    const isMobile =
      this.width < 750;

    const count =
      isMobile
        ? 240
        : 440;

    this.stars = [];

    for (
      let index = 0;
      index < count;
      index += 1
    ) {
      this.stars.push({
        angle:
          Math.random() *
          Math.PI *
          2,

        radius:
          Math.pow(
            Math.random(),
            1.8
          ),

        speed:
          0.65 +
          Math.random() *
          1.3,

        depth:
          0.2 +
          Math.random() *
          0.8,

        size:
          0.35 +
          Math.random() *
          1.4,

        gold:
          Math.random() <
          0.36,

        bright:
          Math.random() <
          0.09,

        phase:
          Math.random() *
          Math.PI *
          2
      });
    }
  }

  drawBackground() {
    const ctx =
      this.ctx;

    ctx.fillStyle =
      '#020202';

    ctx.fillRect(
      0,
      0,
      this.width,
      this.height
    );

    const gradient =
      ctx.createRadialGradient(
        this.centerX,
        this.centerY,
        0,
        this.centerX,
        this.centerY,
        Math.max(
          this.width,
          this.height
        ) * 0.82
      );

    gradient.addColorStop(
      0,
      '#0b0906'
    );

    gradient.addColorStop(
      0.28,
      '#070604'
    );

    gradient.addColorStop(
      0.65,
      '#030303'
    );

    gradient.addColorStop(
      1,
      '#010101'
    );

    ctx.fillStyle =
      gradient;

    ctx.fillRect(
      0,
      0,
      this.width,
      this.height
    );
  }

  drawStar(
    star,
    progress,
    timestamp
  ) {
    const diagonal =
      Math.hypot(
        this.width,
        this.height
      );

    const acceleration =
      Math.pow(
        progress,
        2.45
      );

    const startRadius =
      8 +
      star.radius *
      diagonal *
      0.25;

    const travel =
      acceleration *
      diagonal *
      0.72 *
      star.speed;

    const radius =
      startRadius +
      travel;

    const cos =
      Math.cos(
        star.angle
      );

    const sin =
      Math.sin(
        star.angle
      );

    const x =
      this.centerX +
      cos * radius;

    const y =
      this.centerY +
      sin * radius;

    if (
      x < -150 ||
      x > this.width + 150 ||
      y < -150 ||
      y > this.height + 150
    ) {
      return;
    }

    const trailLength =
      progress > 0.12
        ? (
            4 +
            Math.pow(
              progress,
              2
            ) *
            145 *
            star.depth *
            star.speed
          )
        : 0;

    const previousRadius =
      Math.max(
        0,
        radius -
        trailLength
      );

    const previousX =
      this.centerX +
      cos *
      previousRadius;

    const previousY =
      this.centerY +
      sin *
      previousRadius;

    const pulse =
      0.78 +
      Math.sin(
        timestamp *
        0.004 +
        star.phase
      ) *
      0.22;

    const opacity =
      Math.min(
        1,
        (
          0.45 +
          progress *
          0.65
        ) *
        pulse
      );

    if (
      trailLength > 1
    ) {
      const gradient =
        this.ctx.createLinearGradient(
          previousX,
          previousY,
          x,
          y
        );

      if (star.gold) {
        gradient.addColorStop(
          0,
          'rgba(214,164,72,0)'
        );

        gradient.addColorStop(
          1,
          `rgba(242,204,127,${opacity})`
        );
      } else {
        gradient.addColorStop(
          0,
          'rgba(255,255,255,0)'
        );

        gradient.addColorStop(
          1,
          `rgba(255,255,255,${opacity})`
        );
      }

      this.ctx.beginPath();

      this.ctx.moveTo(
        previousX,
        previousY
      );

      this.ctx.lineTo(
        x,
        y
      );

      this.ctx.strokeStyle =
        gradient;

      this.ctx.lineWidth =
        Math.max(
          0.4,
          star.size * 0.65
        );

      this.ctx.stroke();
    }

    this.ctx.beginPath();

    this.ctx.arc(
      x,
      y,
      Math.max(
        0.4,
        star.size *
        (
          0.65 +
          progress *
          0.6
        )
      ),
      0,
      Math.PI * 2
    );

    this.ctx.fillStyle =
      star.gold
        ? `rgba(240,201,125,${opacity})`
        : `rgba(255,255,255,${opacity})`;

    this.ctx.fill();

    if (
      star.bright &&
      progress > 0.2
    ) {
      const flare =
        4 +
        progress *
        7;

      this.ctx.beginPath();

      this.ctx.moveTo(
        x - flare,
        y
      );

      this.ctx.lineTo(
        x + flare,
        y
      );

      this.ctx.moveTo(
        x,
        y - flare
      );

      this.ctx.lineTo(
        x,
        y + flare
      );

      this.ctx.strokeStyle =
        star.gold
          ? `rgba(240,201,125,${opacity * 0.48})`
          : `rgba(255,255,255,${opacity * 0.42})`;

      this.ctx.lineWidth =
        0.6;

      this.ctx.stroke();
    }
  }

  drawCenterGlow(
    progress
  ) {
    const strength =
      Math.max(
        0,
        1 -
        Math.abs(
          progress -
          0.55
        ) *
        3
      );

    if (
      strength <= 0
    ) {
      return;
    }

    const radius =
      50 +
      progress *
      190;

    const glow =
      this.ctx
        .createRadialGradient(
          this.centerX,
          this.centerY,
          0,
          this.centerX,
          this.centerY,
          radius
        );

    glow.addColorStop(
      0,
      `rgba(255,237,193,${0.18 * strength})`
    );

    glow.addColorStop(
      0.2,
      `rgba(221,172,83,${0.11 * strength})`
    );

    glow.addColorStop(
      1,
      'rgba(0,0,0,0)'
    );

    this.ctx.fillStyle =
      glow;

    this.ctx.fillRect(
      this.centerX -
      radius,
      this.centerY -
      radius,
      radius * 2,
      radius * 2
    );
  }

  render(timestamp) {
    if (
      this.finished
    ) {
      return;
    }

    if (
      this.startTime ===
      null
    ) {
      this.startTime =
        timestamp;
    }

    const elapsed =
      timestamp -
      this.startTime;

    const progress =
      Math.min(
        1,
        elapsed /
        this.animationDuration
      );

    this.drawBackground();

    this.stars.forEach(
      (star) => {
        this.drawStar(
          star,
          progress,
          timestamp
        );
      }
    );

    this.drawCenterGlow(
      progress
    );

    if (
      progress < 1 &&
      !this.finished
    ) {
      this.animationFrame =
        requestAnimationFrame(
          this.render
        );
    }
  }

  unlockPage() {
    document.documentElement
      .classList.remove(
        'zodiac-intro-lock'
      );

    if (document.body) {
      document.body
        .classList.remove(
          'zodiac-intro-lock'
        );

      document.body
        .style.removeProperty(
          'overflow'
        );
    }

    document.documentElement
      .style.removeProperty(
        'overflow'
      );
  }

  finish() {
    if (
      this.finished
    ) {
      return;
    }

    this.finished = true;

    if (
      this.animationFrame
    ) {
      cancelAnimationFrame(
        this.animationFrame
      );

      this.animationFrame =
        null;
    }

    window.removeEventListener(
      'resize',
      this.resize
    );

    /*
     * Sayfayı fade başlamadan önce aç.
     */
    this.unlockPage();

    if (
      !this.intro ||
      !this.intro.isConnected
    ) {
      return;
    }

    /*
     * Intro, hazır sayfanın üzerinde
     * kısa şekilde fade olur.
     */
    this.intro.classList.add(
      'is-ending'
    );

    window.setTimeout(
      () => {
        if (
          this.intro &&
          this.intro.isConnected
        ) {
          this.intro.remove();
        }
      },
      320
    );
  }

  forceRemove() {
    this.finished = true;

    if (
      this.animationFrame
    ) {
      cancelAnimationFrame(
        this.animationFrame
      );

      this.animationFrame =
        null;
    }

    window.removeEventListener(
      'resize',
      this.resize
    );

    this.unlockPage();

    if (
      this.intro &&
      this.intro.isConnected
    ) {
      this.intro.remove();
    }
  }

  play() {
    if (
      !this.shouldPlay()
    ) {
      this.unlockPage();

      if (
        this.intro &&
        this.intro.isConnected
      ) {
        this.intro.remove();
      }

      return;
    }

    try {
      if (
        !this.prepare()
      ) {
        this.forceRemove();
        return;
      }

      document.documentElement
        .classList.add(
          'zodiac-intro-lock'
        );

      this.resize();
      this.createStars();

      window.addEventListener(
        'resize',
        this.resize,
        {
          passive: true
        }
      );

      /*
       * Intro hemen görünür.
       */
      this.intro.classList.add(
        'is-visible'
      );

      requestAnimationFrame(
        () => {
          this.animationFrame =
            requestAnimationFrame(
              this.render
            );
        }
      );

      this.markSeen();

      /*
       * Toplam intro:
       * 2 saniye.
       */
      window.setTimeout(
        () => {
          this.finish();
        },
        2000
      );

      /*
       * Güvenlik watchdog.
       */
      window.setTimeout(
        () => {
          if (
            this.intro &&
            this.intro.isConnected
          ) {
            this.forceRemove();
          }
        },
        2500
      );
    } catch (error) {
      console.error(
        '[Zodiac Intro]',
        error
      );

      this.forceRemove();
    }
  }

  destroy() {
    this.forceRemove();
  }
}


/* =========================================================
   INITIALIZATION
   ========================================================= */

function initializeZodiacSeries(
  scope = document
) {
  const sections =
    scope.matches?.(
      '[data-zodiac-series]'
    )
      ? [scope]
      : Array.from(
          scope.querySelectorAll(
            '[data-zodiac-series]'
          )
        );

  sections.forEach(
    (section) => {
      if (
        section.dataset
          .zodiacInitialized ===
        'true'
      ) {
        return;
      }

      section.dataset
        .zodiacInitialized =
        'true';

      section.zodiacSeriesInstance =
        new ZodiacSeries(
          section
        );

      section.zodiacIntroInstance =
        new ZodiacIntro(
          section
        );

      section
        .zodiacIntroInstance
        .play();
    }
  );
}


/* =========================================================
   DOM READY
   ========================================================= */

if (
  document.readyState ===
  'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializeZodiacSeries();
    },
    {
      once: true
    }
  );
} else {
  initializeZodiacSeries();
}


/* =========================================================
   SHOPIFY THEME EDITOR
   ========================================================= */

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initializeZodiacSeries(
      event.target
    );
  }
);

document.addEventListener(
  'shopify:section:unload',
  (event) => {
    const section =
      event.target.matches?.(
        '[data-zodiac-series]'
      )
        ? event.target
        : event.target.querySelector?.(
            '[data-zodiac-series]'
          );

    if (
      section
        ?.zodiacSeriesInstance
    ) {
      section
        .zodiacSeriesInstance
        .destroy();
    }

    if (
      section
        ?.zodiacIntroInstance
    ) {
      section
        .zodiacIntroInstance
        .destroy();
    }
  }
);